import upperArchIcon from '../../../../assets/icons/upper-arch.svg';
import lowerArchIcon from '../../../../assets/icons/lower-arch.svg';
import bothArchsIcon from '../../../../assets/icons/both-archs.svg';
import type { PatientFile } from '../../../../services/cloud-files';
import FileUploadCard from '../../../../components/upload/FileUploadCard';

type UploadMode = 'files' | 'scanId';

function isDeliveryUpper(file: PatientFile): boolean {
    const meta = file.metadata as Record<string, unknown> | null;
    return meta?.delivery === true && meta?.scanType === 'upper';
}

function isDeliveryLower(file: PatientFile): boolean {
    const meta = file.metadata as Record<string, unknown> | null;
    return meta?.delivery === true && meta?.scanType === 'lower';
}

function isDeliveryBite(file: PatientFile): boolean {
    const meta = file.metadata as Record<string, unknown> | null;
    return meta?.delivery === true && meta?.scanType === 'bite';
}

interface StarterScanSectionProps {
    patientId: string | null;
    mode: UploadMode;
    onModeChange: (mode: UploadMode) => void;
    scanId: string;
    onScanIdChange: (scanId: string) => void;
    /** Called whenever the per-card file presence changes; index.tsx derives filesReady from these. */
    onUpperFilesChange: (files: PatientFile[]) => void;
    onLowerFilesChange: (files: PatientFile[]) => void;
    onBiteFilesChange: (files: PatientFile[]) => void;
    onUploadStateChange?: (isUploading: boolean, delta: 1 | -1) => void;
}

export function StarterScanSection({
    patientId,
    mode,
    onModeChange,
    scanId,
    onScanIdChange,
    onUpperFilesChange,
    onLowerFilesChange,
    onBiteFilesChange,
    onUploadStateChange
}: StarterScanSectionProps) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                Patient scans
                <span className="text-red-500 ml-0.5">*</span>
            </label>

            <p className="text-xs text-gray-600 mb-3">
                This is a starter aligners case. Please perform the SDI instructions and share your
                latest patient scans with us below.
            </p>

            {/* Mode pill toggle */}
            <div className="flex bg-gray-100 rounded-full p-1 mb-3">
                <button
                    type="button"
                    onClick={() => onModeChange('files')}
                    className={`flex-1 px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                        mode === 'files'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Upload scans
                </button>
                <button
                    type="button"
                    onClick={() => onModeChange('scanId')}
                    className={`flex-1 px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 ${
                        mode === 'scanId'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                    Enter scan ID
                </button>
            </div>

            {mode === 'files' && (
                <div className="grid grid-cols-3 gap-3">
                    <FileUploadCard
                        label="Upper STL"
                        icon={upperArchIcon}
                        patientId={patientId ?? undefined}
                        fileType="STL"
                        accept=".stl"
                        compact
                        description="Upper STL"
                        metadata={{ delivery: true, scanType: 'upper' }}
                        fileFilter={isDeliveryUpper}
                        onUploadedFilesChange={onUpperFilesChange}
                        onUploadStateChange={onUploadStateChange}
                    />
                    <FileUploadCard
                        label="Lower STL"
                        icon={lowerArchIcon}
                        patientId={patientId ?? undefined}
                        fileType="STL"
                        accept=".stl"
                        compact
                        description="Lower STL"
                        metadata={{ delivery: true, scanType: 'lower' }}
                        fileFilter={isDeliveryLower}
                        onUploadedFilesChange={onLowerFilesChange}
                        onUploadStateChange={onUploadStateChange}
                    />
                    <FileUploadCard
                        label="Bite scans"
                        icon={bothArchsIcon}
                        patientId={patientId ?? undefined}
                        fileType="STL"
                        accept=".stl,image/*"
                        compact
                        description="Bite scan"
                        metadata={{ delivery: true, scanType: 'bite' }}
                        fileFilter={isDeliveryBite}
                        onUploadedFilesChange={onBiteFilesChange}
                        onUploadStateChange={onUploadStateChange}
                    />
                </div>
            )}

            {mode === 'scanId' && (
                <input
                    type="text"
                    value={scanId}
                    onChange={(e) => onScanIdChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 transition-colors"
                    placeholder="Enter scan ID"
                />
            )}
        </div>
    );
}
